import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { TrendingUp, MousePointerClick, IndianRupee, Users, Loader2 } from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";

const PLATFORM_COLORS = {
  meta: "#1877F2", instagram: "#E4405F", facebook: "#0866FF",
  google: "#EA4335", youtube: "#FF0000", other: "#6B7280",
};
const FOREST = "#064E3B", GOLD = "#D4AF37";

export default function PublicBuilderReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/builder-report/${token}`);
        setData(r.data);
      } catch (e) {
        setError(formatApiError(e));
      }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center text-center px-6" data-testid="report-error">
        <h1 className="font-heading font-extrabold text-3xl text-forest">Link unavailable</h1>
        <p className="text-sm text-muted-foreground mt-2">{error || "This report link may have expired."}</p>
      </div>
    );
  }
  const { builder, projects, summary } = data;
  const t = summary?.totals || {};
  return (
    <div className="min-h-screen bg-cream" data-testid="public-builder-report">
      <header className="bg-forest text-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4">
            <img src="/brand/ofr-mark.png" alt="OFR" className="w-14 h-14 rounded-lg object-cover" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold">Ad performance report</div>
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl">{builder.name}</h1>
              {builder.company && <div className="text-white/70 text-sm">{builder.company}</div>}
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Impressions" value={(t.impressions || 0).toLocaleString("en-IN")} Icon={TrendingUp} />
          <Stat label="Clicks" value={(t.clicks || 0).toLocaleString("en-IN")} Icon={MousePointerClick} />
          <Stat label="Ad spend" value={formatPrice(t.spend_inr || 0)} Icon={IndianRupee} />
          <Stat label="Leads generated" value={t.conversions || 0} Icon={Users} gold />
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl border border-border bg-white p-5">
            <div className="text-xs uppercase tracking-widest text-gold font-bold mb-3">Spend & leads timeline</div>
            {summary?.time_series?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={summary.time_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line yAxisId="left" type="monotone" dataKey="spend" stroke={FOREST} strokeWidth={2} name="Spend (₹)" />
                  <Line yAxisId="right" type="monotone" dataKey="conversions" stroke={GOLD} strokeWidth={2} name="Leads" />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="py-12 text-center text-sm text-muted-foreground">No campaigns yet.</div>}
          </div>
          <div className="rounded-xl border border-border bg-white p-5">
            <div className="text-xs uppercase tracking-widest text-gold font-bold mb-3">By platform</div>
            {summary?.by_platform?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={summary.by_platform} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="platform" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="spend_inr" name="Spend (₹)">
                    {summary.by_platform.map((p) => <Cell key={p.platform} fill={PLATFORM_COLORS[p.platform] || "#888"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="py-12 text-center text-sm text-muted-foreground">No data.</div>}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-gold font-bold mb-3">Projects covered ({projects?.length || 0})</div>
          {projects?.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-3">
              {projects.map((p) => (
                <div key={p.id} className="rounded-lg border border-border p-3">
                  <div className="font-heading font-bold text-forest">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.location} · {p.project_type}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground text-center py-4">No projects assigned yet.</div>}
        </div>
        <div className="text-center text-[11px] text-muted-foreground">
          Powered by <span className="font-bold text-forest">Our First Realty</span> · Real-estate exchange for Hyderabad
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, Icon, gold }) {
  return (
    <div className={`rounded-xl p-4 border ${gold ? "bg-gold/10 border-gold/40" : "bg-white border-border"}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-heading font-extrabold text-2xl text-forest">{value}</div>
    </div>
  );
}
